// The mode catalog and the promotion gate. Pure, dependency-free, tested.
//
// The copy here is the deliverable, not filler. It is the only place the owner
// is told what actually changes the moment they widen the agent's autonomy, and
// it lives in a pure module so it can be asserted rather than drifting.

import type { CmMode } from './types'

export const CM_MODES: CmMode[] = ['off', 'shadow', 'draft', 'tiered', 'autonomous']

export const MODE_RANK: Record<CmMode, number> = {
  off: 0, shadow: 1, draft: 2, tiered: 3, autonomous: 4,
}

export interface ModeEntry {
  value: CmMode
  title: string
  /** What the agent does while in this mode. */
  what: string
  /** What changes THE MOMENT you flip to it — the part people skip. */
  onFlip: string
  sends: boolean
}

export const MODE_CATALOG: ModeEntry[] = [
  {
    value: 'off',
    title: 'Off',
    what: "The agent doesn't run. Nothing is watched, nothing is proposed, nothing is sent.",
    onFlip:
      'The next scheduled run is skipped. Anything already in the queue stays there, undecided, until you come back.',
    sends: false,
  },
  {
    value: 'shadow',
    title: 'Shadow',
    what: 'The agent runs and writes down what it would have done. It sends nothing to anyone.',
    onFlip:
      'Runs resume. Every proposal lands in the Inbox marked "not sent", and you can mark each one good or bad. Those marks are the evidence that unlocks the next mode.',
    sends: false,
  },
  {
    value: 'draft',
    title: 'Draft',
    what: 'Every action queues for your approval. Nothing reaches a member unless you press Approve.',
    onFlip:
      'Proposals become approvable. Anything already logged in shadow stays logged — it is not retroactively sendable.',
    sends: true,
  },
  {
    value: 'tiered',
    title: 'Tiered',
    what:
      'Low-risk, templated, one-member actions send on their own. Broadcasts and anything free-text still wait for you.',
    onFlip:
      'Messages start going out without you — but only kinds on the allowlist below, only to one member at a time, only inside the caps and outside quiet hours. Review that list before you confirm.',
    sends: true,
  },
  {
    value: 'autonomous',
    title: 'Autonomous',
    what:
      'The agent sends on its own, held only by the kill switch, the caps and quiet hours.',
    onFlip:
      'Every kind can send without you, including a post the whole community sees. The kill switch, the caps and quiet hours are the only brakes left. Anything about money, bereavement or moderation still waits for you — that never changes.',
    sends: true,
  },
]

export function modeEntry(m: CmMode): ModeEntry {
  return MODE_CATALOG.find((e) => e.value === m) ?? MODE_CATALOG[0]
}

export type Escalation = 'none' | 'confirm' | 'typed'

/**
 * How much friction a mode change deserves.
 *
 * De-escalation is ALWAYS instant and silent. The brake must never be gated
 * behind a dialog — the moment someone wants the agent to stop, stopping should
 * cost one tap. Widening is the destructive direction here, which inverts the
 * rule the Jaguar-Owned controls page uses (there, narrowing was destructive).
 */
export function escalationFor(from: CmMode, to: CmMode): Escalation {
  if (MODE_RANK[to] <= MODE_RANK[from]) return 'none'
  if (to === 'autonomous') return 'typed'
  return 'confirm'
}

export const AUTONOMOUS_PHRASE = 'GO AUTONOMOUS'

/** Typed exactly, to clear a queue that cannot be un-cleared. */
export function confirmPhraseFor(queued: number): string {
  return `CANCEL ${queued} QUEUED`
}

export interface PromotionState {
  gradedCount: number
  thumbsUpRate: number      // 0..1
  daysInCurrentMode: number
  rejectionsLast14d: number
  autoApproveKinds: string[]
  anyChannelOn: boolean
  capsAllPositive: boolean
  quietHoursSet: boolean
  dispatchedLowTier: number
  uneditedRate: number      // 0..1
  hasMeasuredDelivery: boolean
}

export interface PromotionVerdict {
  ok: boolean
  blockers: string[]
}

/**
 * The gate that turns "extend trust over time" from an intention into a
 * mechanism. The Segmented option stays disabled until these hold.
 *
 * The numbers are deliberately conservative for a community of roughly a dozen
 * active members: at this size one bad message is noticed by everybody, and
 * there is no crowd for a mistake to disappear into.
 */
export function canPromoteTo(target: CmMode, s: PromotionState): PromotionVerdict {
  const blockers: string[] = []

  if (target === 'off' || target === 'shadow' || target === 'draft') {
    return { ok: true, blockers }
  }

  if (!s.anyChannelOn) blockers.push('Turn on at least one channel first.')
  if (!s.capsAllPositive) blockers.push('Every frequency cap is 0 — the agent could not send anything.')
  if (!s.quietHoursSet) blockers.push('Set quiet hours and a timezone first.')

  if (target === 'tiered') {
    if (s.gradedCount < 10) {
      blockers.push(`Mark at least 10 proposals good or bad (you have ${s.gradedCount}).`)
    }
    if (s.thumbsUpRate < 0.8) {
      blockers.push(`At least 80% of marked proposals must be good (currently ${Math.round(s.thumbsUpRate * 100)}%).`)
    }
    if (s.autoApproveKinds.length === 0) {
      blockers.push('Pick at least one kind to auto-send — an empty allowlist makes Tiered behave exactly like Draft.')
    }
    return { ok: blockers.length === 0, blockers }
  }

  // autonomous
  const tieredFirst = canPromoteTo('tiered', s)
  blockers.push(...tieredFirst.blockers)
  if (s.daysInCurrentMode < 30) {
    blockers.push(`Spend at least 30 days in Tiered or Draft first (you are ${s.daysInCurrentMode} days in).`)
  }
  if (s.rejectionsLast14d > 0) {
    blockers.push(`No rejections in the last 14 days (there ${s.rejectionsLast14d === 1 ? 'was 1' : `were ${s.rejectionsLast14d}`}).`)
  }
  if (s.dispatchedLowTier < 20) {
    blockers.push(`At least 20 low-risk messages must have gone out (you have ${s.dispatchedLowTier}).`)
  }
  if (s.uneditedRate < 0.95) {
    blockers.push(`At least 95% of those must have gone out unedited (currently ${Math.round(s.uneditedRate * 100)}%).`)
  }
  if (!s.hasMeasuredDelivery) {
    blockers.push('No channel has a measured delivery receipt yet — you cannot verify anything actually arrives.')
  }
  return { ok: blockers.length === 0, blockers }
}
